// v2/components/admin/UserFormModal.js — musteri kullanicisi ekleme / duzenleme
// Parola duz metin saklanir (mevcut sistemle ayni).
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  X, Save, Loader2, Eye, EyeOff, Dice5, AlertTriangle, KeyRound,
} from 'lucide-react';
import { adminUserApi } from '../../hooks/useAdminCustomer';
import s from './UserFormModal.module.css';

const BOS = {
  ad_soyad: '', mail: '', parola: '', gorev: '', meslek: '',
  cep_telefonu: '', dahili_no: '', dogum_tarihi: '',
};

export default function UserFormModal({ acik, kod, kullanici, onKapat, onKaydedildi }) {
  const duzenleme = Boolean(kullanici);
  const [form, setForm] = useState(BOS);
  const [parolaGoster, setParolaGoster] = useState(false);
  const [kaydediyor, setKaydediyor] = useState(false);
  const [hata, setHata] = useState(null);
  const ilkAlan = useRef(null);

  useEffect(() => {
    if (!acik) return;
    setHata(null);
    setParolaGoster(false);
    setForm(duzenleme ? {
      ad_soyad: kullanici.ad_soyad || '',
      mail: kullanici.mail || '',
      parola: '',
      gorev: kullanici.gorev || '',
      meslek: kullanici.meslek || '',
      cep_telefonu: kullanici.phone || '',
      dahili_no: kullanici.ext || '',
      dogum_tarihi: kullanici.birth_date || '',
    } : BOS);
    const t = setTimeout(() => { if (ilkAlan.current) ilkAlan.current.focus(); }, 60);
    return () => clearTimeout(t);
  }, [acik, duzenleme, kullanici]);

  useEffect(() => {
    if (!acik) return undefined;
    const esc = (e) => { if (e.key === 'Escape' && !kaydediyor) onKapat(); };
    const onceki = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', esc);
    return () => {
      document.body.style.overflow = onceki;
      document.removeEventListener('keydown', esc);
    };
  }, [acik, kaydediyor, onKapat]);

  const degis = useCallback((alan) => (e) => {
    const v = e.target.value;
    setForm((f) => ({ ...f, [alan]: v }));
  }, []);

  const parolaUret = useCallback(async () => {
    try {
      const v = await adminUserApi.parolaOner();
      if (v && v.password) {
        setForm((f) => ({ ...f, parola: v.password }));
        setParolaGoster(true);
      }
    } catch (e) {
      setHata('Parola üretilemedi');
    }
  }, []);

  const kaydet = useCallback(async () => {
    if (kaydediyor) return;
    setHata(null);
    const ad = form.ad_soyad.trim();
    const mail = form.mail.trim();
    if (!ad) { setHata('Ad soyad zorunlu'); return; }
    if (!mail) { setHata('E-posta zorunlu'); return; }
    if (!duzenleme && form.parola.length < 6) { setHata('Parola en az 6 karakter olmalı'); return; }
    if (duzenleme && form.parola && form.parola.length < 6) {
      setHata('Parola en az 6 karakter olmalı'); return;
    }

    const govde = {
      ad_soyad: ad, mail,
      gorev: form.gorev.trim(), meslek: form.meslek.trim(),
      cep_telefonu: form.cep_telefonu.trim(), dahili_no: form.dahili_no.trim(),
      dogum_tarihi: form.dogum_tarihi || '',
    };
    if (form.parola) govde.parola = form.parola;

    setKaydediyor(true);
    try {
      const v = duzenleme
        ? await adminUserApi.guncelle(kullanici.id, govde)
        : await adminUserApi.ekle(kod, govde);
      if (!v || !v.success) throw new Error((v && v.message) || 'Kaydedilemedi');
      onKaydedildi(v.message || (duzenleme ? 'Kullanıcı güncellendi' : 'Kullanıcı eklendi'));
    } catch (e) {
      setHata((e && e.message) || 'Kaydedilemedi');
    } finally {
      setKaydediyor(false);
    }
  }, [form, duzenleme, kullanici, kod, kaydediyor, onKaydedildi]);

  const gonder = (e) => { e.preventDefault(); kaydet(); };

  if (!acik) return null;

  return (
    <div className={s.overlay} role="dialog" aria-modal="true" aria-label={duzenleme ? 'Kullanıcı düzenle' : 'Yeni kullanıcı'}>
      <div className={s.backdrop} onClick={() => !kaydediyor && onKapat()} aria-hidden="true" />
      <div className={s.modal}>
        <div className={s.head}>
          <div className={s.headText}>
            <h3 className={s.title}>{duzenleme ? 'Kullanıcıyı Düzenle' : 'Yeni Kullanıcı'}</h3>
            <p className={s.sub}>Müşteri kodu <b>{kod}</b></p>
          </div>
          <button type="button" className={s.closeBtn} onClick={onKapat} disabled={kaydediyor} aria-label="Kapat">
            <X size={18} />
          </button>
        </div>

        <form className={s.body} onSubmit={gonder}>
          {hata && (
            <div className={s.error}><AlertTriangle size={16} /> <span>{hata}</span></div>
          )}

          <div className={s.grid}>
            <label className={`${s.field} ${s.wide}`}>
              <span className={s.label}>Ad Soyad <i className={s.req}>*</i></span>
              <input
                ref={ilkAlan} className={s.input} value={form.ad_soyad}
                onChange={degis('ad_soyad')} placeholder="Ad ve soyad" maxLength={100}
              />
            </label>
            <label className={`${s.field} ${s.wide}`}>
              <span className={s.label}>E-posta <i className={s.req}>*</i></span>
              <input
                className={s.input} type="email" value={form.mail}
                onChange={degis('mail')} placeholder="ornek@firma.com" maxLength={254}
                autoComplete="off"
              />
            </label>

            <label className={`${s.field} ${s.wide}`}>
              <span className={s.label}>
                <KeyRound size={13} /> Parola {duzenleme
                  ? <i className={s.hint}>boş bırakılırsa değişmez</i>
                  : <i className={s.req}>*</i>}
              </span>
              <span className={s.pwRow}>
                <input
                  className={s.input} type={parolaGoster ? 'text' : 'password'}
                  value={form.parola} onChange={degis('parola')}
                  placeholder={duzenleme ? 'Yeni parola' : 'En az 6 karakter'}
                  autoComplete="new-password"
                />
                <button
                  type="button" className={s.iconBtn}
                  onClick={() => setParolaGoster((v) => !v)}
                  aria-label={parolaGoster ? 'Parolayı gizle' : 'Parolayı göster'}
                >
                  {parolaGoster ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
                <button type="button" className={s.genBtn} onClick={parolaUret}>
                  <Dice5 size={15} /> <span className={s.genText}>Üret</span>
                </button>
              </span>
              {duzenleme && form.parola && (
                <span className={s.warn}>Parola değişince kullanıcının oturumu kapanır.</span>
              )}
            </label>

            <label className={s.field}>
              <span className={s.label}>Görev</span>
              <input className={s.input} value={form.gorev} onChange={degis('gorev')} maxLength={100} />
            </label>
            <label className={s.field}>
              <span className={s.label}>Meslek</span>
              <input className={s.input} value={form.meslek} onChange={degis('meslek')} maxLength={100} />
            </label>
            <label className={s.field}>
              <span className={s.label}>Cep Telefonu</span>
              <input
                className={s.input} value={form.cep_telefonu} onChange={degis('cep_telefonu')}
                placeholder="5xx xxx xx xx" maxLength={20} inputMode="tel"
              />
            </label>
            <label className={s.field}>
              <span className={s.label}>Dahili No</span>
              <input className={s.input} value={form.dahili_no} onChange={degis('dahili_no')} maxLength={20} />
            </label>
            <label className={s.field}>
              <span className={s.label}>Doğum Tarihi</span>
              <input className={s.input} type="date" value={form.dogum_tarihi} onChange={degis('dogum_tarihi')} />
            </label>
          </div>
        </form>

        <div className={s.foot}>
          <button type="button" className={s.cancelBtn} onClick={onKapat} disabled={kaydediyor}>İptal</button>
          <button type="button" className={s.saveBtn} onClick={kaydet} disabled={kaydediyor}>
            {kaydediyor ? <Loader2 size={16} className={s.spin} /> : <Save size={16} />}
            {kaydediyor ? 'Kaydediliyor…' : 'Kaydet'}
          </button>
        </div>
      </div>
    </div>
  );
}
